import { useContext, useMemo, useState } from 'react'
import { Check, ChevronDown, X } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { usePageInfo, useTeamAuditData } from 'app/frontend/base/config/queries'
import { updateTierPriority } from 'app/frontend/base/api/character'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { LootContext } from '../context'
import { seasonWishlistMetadata, withSeasonWishlistMetadata } from '../helpers'
import Season from 'app/frontend/types/custom/Season'
import UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import SeasonSelect from './SeasonSelect'
import NoMemberWarning from './NoMemberWarning'
import TierPrioritiesSummary from './TierPrioritiesSummary'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import { cn } from '@/lib/utils'
import { itemSlotLabels } from 'app/frontend/types/custom/ItemSlot'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import {
  armorTypeForClass,
  armorTypeOrder,
  effectivePriority,
  isAutoObtained,
  MemberRow,
  obtainedCount,
  obtainedCountColorClass,
  priorityOptions,
  PriorityValue,
  seasonCatalystCharge,
  seasonTierItems,
  TierSlot,
  tierSlots,
} from './tierPriorityHelpers'

const PriorityBadge = ({
  value,
  className,
}: {
  value: string
  className?: string
}) => {
  const option = priorityOptions.find((candidate) => candidate.value === value)
  const background =
    value === 'unset'
      ? 'bg-muted'
      : option?.background
          .split(' ')
          .filter((token) => token.startsWith('bg-'))
          .join(' ')

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium',
        option?.className,
        background,
        className,
      )}
    >
      {option?.label ?? value}
    </span>
  )
}

const PriorityCell = ({
  row,
  slot,
  canEdit,
  onChange,
  pending,
  minimumTrack,
}: {
  row: MemberRow
  slot: TierSlot
  canEdit: boolean
  onChange: (value: string) => void
  pending: boolean
  minimumTrack: UpgradeTrack
}) => {
  const value = row.priorities[slot]
  const autoObtained = isAutoObtained(slot, row.tierItems, minimumTrack)

  if (!canEdit || autoObtained) {
    return <PriorityBadge value={value} />
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={pending}>
        <button
          type='button'
          className='inline-flex items-center gap-1 rounded hover:opacity-80 disabled:opacity-50'
        >
          <PriorityBadge value={value} />
          <ChevronDown className='h-3 w-3 text-muted-foreground' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='center'>
        {priorityOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
          >
            <PriorityBadge value={option.value} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const TierPrioritiesOverview = () => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { selectedSeason, setSelectedSeason } = useContext(LootContext)
  const {
    data: {
      currentExpansion: { seasons },
      currentSeason,
      user,
    },
  } = usePageInfo()
  const { data: auditData, isPending } = useTeamAuditData(
    team.settings.tierPriorityEnabled.value ? team.id : undefined,
  )
  const [armorFilter, setArmorFilter] = useState<string>('all')
  const teamQueryKey = ['team', { path: team.path }]

  const minimumTrack = team.settings.tierPriorityMinimumTrack
    .value as UpgradeTrack

  const canEditCharacter = (character: wowaudit.CharacterReference) => {
    if (accessLevel === 'admin') return true
    if (team.settings.wishlistLocked.value) return false
    return !!user && character.userId === user.id
  }

  const mutation = useMutation({
    mutationFn: ({
      characterId,
      nextPriority,
    }: {
      characterId: number
      nextPriority: Record<string, string>
    }) =>
      updateTierPriority(team.id, characterId, selectedSeason.id, nextPriority),
    onMutate: async ({ characterId, nextPriority }) => {
      await queryClient.cancelQueries({ queryKey: teamQueryKey })
      const previousTeam = queryClient.getQueryData<wowaudit.Team>(teamQueryKey)

      queryClient.setQueryData<wowaudit.Team>(teamQueryKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.map((m) =>
                m.characterReference.id === characterId
                  ? {
                      ...m,
                      characterReference: {
                        ...m.characterReference,
                        wishlistMetadata: withSeasonWishlistMetadata(
                          m.characterReference.wishlistMetadata,
                          selectedSeason.id,
                          { tier_priority: nextPriority },
                        ),
                      },
                    }
                  : m,
              ),
            }
          : old,
      )

      return { previousTeam }
    },
    onError: (error, _variables, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamQueryKey, context.previousTeam)
      }
      displayErrorToast(error, t)
    },
    onSuccess: (newTeam) => {
      queryClient.setQueryData(teamQueryKey, newTeam)
    },
  })

  const setSlotPriority = (
    characterId: number,
    storedPriority: Record<string, string>,
    slot: TierSlot,
    value: string,
  ) => {
    if (mutation.isPending) return

    const next = { ...storedPriority }
    if (!value || value === 'unset') {
      delete next[slot]
    } else {
      next[slot] = value
    }
    mutation.mutate({ characterId, nextPriority: next })
  }

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      []
    )
  }, [team.members])

  const rows = useMemo<MemberRow[]>(() => {
    return visibleMembers.map((member) => {
      const character = member.characterReference
      const storedPriority = seasonWishlistMetadata(
        character.wishlistMetadata,
        selectedSeason.id,
      ).tier_priority
      const auditRow = auditData?.[character.id]
      const tierItems = seasonTierItems(auditRow, selectedSeason.id)
      const priorities = Object.fromEntries(
        tierSlots.map((slot) => [
          slot,
          effectivePriority(slot, storedPriority, tierItems, minimumTrack),
        ]),
      ) as Record<TierSlot, PriorityValue | 'unset'>

      return {
        member,
        armorType: armorTypeForClass(character.characterClass),
        obtained: obtainedCount(storedPriority, tierItems, minimumTrack),
        feat: seasonCatalystCharge(
          auditRow,
          selectedSeason.id,
          currentSeason.id,
        ),
        priorities,
        storedPriority,
        tierItems,
      }
    })
  }, [
    visibleMembers,
    auditData,
    minimumTrack,
    selectedSeason.id,
    currentSeason.id,
  ])

  const filteredRows = useMemo(() => {
    if (armorFilter === 'all') return rows
    return rows.filter((row) => row.armorType === armorFilter)
  }, [rows, armorFilter])

  const columns: ColumnDef<MemberRow>[] = useMemo(
    () => [
      {
        ...genericSorting<MemberRow>('text', 'Character'),
        id: 'name',
        size: 175,
        accessorFn: (row) => row.member.characterReference.name,
        cell: (info) => (
          <div className='flex'>
            <TeamMemberLink member={info.row.original.member} iconType='both' />
          </div>
        ),
      },
      {
        ...genericSorting<MemberRow>('alphanumeric', 'Pieces', true),
        id: 'pieces',
        size: 70,
        accessorFn: (row) => row.obtained,
        cell: (info) => (
          <span
            className={cn(
              'font-medium',
              obtainedCountColorClass(info.row.original.obtained),
            )}
          >
            {info.row.original.obtained} / {tierSlots.length}
          </span>
        ),
      },
      {
        ...genericSorting<MemberRow>(
          'alphanumeric',
          <Tooltip>
            <TooltipTrigger className='cursor-help border-b border-dotted border-muted-foreground'>
              Catalyst
            </TooltipTrigger>
            <TooltipContent className='max-w-64'>
              Whether the{' '}
              {selectedSeason.catalyst_charge_achievement_id ? (
                <a
                  href={`https://wowhead.com/achievement=${selectedSeason.catalyst_charge_achievement_id}`}
                  target='_blank'
                  rel='noreferrer'
                  className='text-primary'
                  onClick={(event) => event.stopPropagation()}
                >
                  Serpent Scion
                </a>
              ) : (
                'Serpent Scion'
              )}{' '}
              achievement is completed (grants a Catalyst charge)
            </TooltipContent>
          </Tooltip>,
          true,
        ),
        id: 'feat',
        size: 60,
        accessorFn: (row) =>
          row.feat === 'yes' ? 2 : row.feat === 'no' ? 1 : 0,
        cell: (info) => (
          <div className='flex items-center justify-center'>
            {info.row.original.feat === 'yes' ? (
              <Check className='w-4 h-4 text-success-foreground' />
            ) : (
              <X className='w-4 h-4 text-destructive-foreground' />
            )}
          </div>
        ),
      },
      ...tierSlots.map(
        (slot): ColumnDef<MemberRow> => ({
          ...genericSorting<MemberRow>('text', itemSlotLabels[slot], true),
          id: slot,
          size: 120,
          accessorFn: (row) => row.priorities[slot],
          cell: (info) => {
            const row = info.row.original
            return (
              <PriorityCell
                row={row}
                slot={slot}
                canEdit={canEditCharacter(row.member.characterReference)}
                pending={mutation.isPending}
                minimumTrack={minimumTrack}
                onChange={(value) =>
                  setSlotPriority(
                    row.member.characterReference.id,
                    row.storedPriority,
                    slot,
                    value,
                  )
                }
              />
            )
          },
        }),
      ),
    ],
    [
      accessLevel,
      user,
      team.settings.wishlistLocked.value,
      mutation.isPending,
      minimumTrack,
      selectedSeason.catalyst_charge_achievement_id,
    ],
  )

  if (!team.settings.tierPriorityEnabled.value) {
    return null
  }

  return (
    <div>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>Tier status</div>
        {seasons.length > 1 && (
          <SeasonSelect
            className='w-52'
            value={selectedSeason.id.toString()}
            setValue={(value) => {
              const newSeason = seasons.find(
                (season) => season.id.toString() === value,
              ) as Season
              setSelectedSeason(newSeason)
            }}
          />
        )}
      </h2>

      {visibleMembers.length === 0 ? (
        <NoMemberWarning className='my-6' section='loot' />
      ) : isPending ? (
        <Skeleton className='h-160 mt-2' />
      ) : (
        <div className='mt-4 flex flex-col lg:flex-row gap-4'>
          <TierPrioritiesSummary
            rows={filteredRows}
            allRows={rows}
            minimumTrack={minimumTrack}
            selectedSeason={selectedSeason}
          />

          <div className='min-w-0 flex-1 space-y-2'>
            <Tabs value={armorFilter} onValueChange={setArmorFilter}>
              <TabsList>
                {['all', ...armorTypeOrder].map((value) => (
                  <TabsTrigger key={value} value={value}>
                    {value === 'all' ? 'All' : value}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <DataTable
              columns={columns}
              data={filteredRows}
              includeHeader={true}
              getRowId={(row) => row.member.id.toString()}
              sortingState={[{ id: 'pieces', desc: true }]}
              emptyMessage={() => 'No characters for this armor type'}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TierPrioritiesOverview
